/**
 * NeoSalesStatus — Super Admin dashboard for NEO feature activation.
 * Lists every location with its NEO ON / Upgrade Pending status, client name,
 * last-updated timestamp, and a single toggle action with confirmation.
 *
 * Mobile-first (390×844), bilingual (EN/FR), optimistic UI.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search, Loader2, Check, X, Power } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;

const getAuthToken = () => localStorage.getItem('yulontap_token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('token');

export default function NeoSalesStatus() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({ total: 0, enabled: 0, disabled: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // 'all' | 'on' | 'pending'
    const [pendingRow, setPendingRow] = useState(null); // row being confirmed
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const token = getAuthToken();
            const res = await fetch(`${API_BASE}/api/neo/sales-status`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('failed');
            const d = await res.json();
            setRows(d.rows || []);
            setCounts({ total: d.total || 0, enabled: d.enabled_count || 0, disabled: d.disabled_count || 0 });
        } catch {
            toast.error(t('neoStatus.loadFailed', 'Failed to load NEO status'));
        }
        setLoading(false);
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter(r => {
            if (filter === 'on' && !r.neo_enabled) return false;
            if (filter === 'pending' && r.neo_enabled) return false;
            if (!q) return true;
            return (r.location_name || '').toLowerCase().includes(q)
                || (r.organization_name || '').toLowerCase().includes(q);
        });
    }, [rows, search, filter]);

    const confirmToggle = async () => {
        if (!pendingRow) return;
        setSubmitting(true);
        const target = !pendingRow.neo_enabled;
        // Optimistic update
        setRows(prev => prev.map(r =>
            r.location_id === pendingRow.location_id
                ? { ...r, neo_enabled: target, last_toggled_at: new Date().toISOString() }
                : r
        ));
        setCounts(c => ({
            ...c,
            enabled: target ? c.enabled + 1 : Math.max(0, c.enabled - 1),
            disabled: target ? Math.max(0, c.disabled - 1) : c.disabled + 1,
        }));
        try {
            const token = getAuthToken();
            const res = await fetch(`${API_BASE}/api/neo/locations/${pendingRow.location_id}/toggle`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ neo_enabled: target }),
            });
            if (!res.ok) throw new Error('toggle failed');
            toast.success(target
                ? t('neoStatus.enabledToast', 'NEO enabled for') + ` ${pendingRow.location_name}`
                : t('neoStatus.disabledToast', 'NEO disabled for') + ` ${pendingRow.location_name}`);
            setPendingRow(null);
        } catch {
            toast.error(t('neoStatus.toggleFailed', 'Failed to toggle NEO. Reloading…'));
            await load();
            setPendingRow(null);
        }
        setSubmitting(false);
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        try {
            const d = parseUtc(iso);
            return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        } catch { return '—'; }
    };

    return (
        <div className="space-y-4 p-3 sm:p-5" data-testid="neo-status-page">
            {/* Header */}
            <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight bg-gradient-to-r from-fuchsia-600 to-purple-600 bg-clip-text text-transparent">
                    {t('neoStatus.title', 'NEO Status')}
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    {t('neoStatus.subtitle', 'Manage NEO activation across all locations.')}
                </p>
            </div>

            {/* Counter pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-xs" data-testid="neo-count-total">
                    <span className="font-semibold">{counts.total}</span>
                    <span className="text-muted-foreground">{t('neoStatus.total', 'Total')}</span>
                </div>
                <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-xs" data-testid="neo-count-on">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="font-semibold text-emerald-700 dark:text-emerald-300">{counts.enabled}</span>
                    <span className="text-emerald-700/80 dark:text-emerald-300/80">{t('neoStatus.on', 'NEO ON')}</span>
                </div>
                <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-500/10 border border-slate-500/30 text-xs" data-testid="neo-count-pending">
                    <span className="w-2 h-2 rounded-full bg-slate-400" />
                    <span className="font-semibold text-slate-700 dark:text-slate-300">{counts.disabled}</span>
                    <span className="text-slate-700/80 dark:text-slate-300/80">{t('neoStatus.pending', 'Upgrade Pending')}</span>
                </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 flex-col sm:flex-row">
                <div className="relative w-full sm:flex-1">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
                    <Input
                        placeholder={t('neoStatus.searchPlaceholder', 'Search location or client…')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-8 min-h-[40px]"
                        data-testid="neo-status-search"
                    />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-full sm:w-44 min-h-[40px]" data-testid="neo-status-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('neoStatus.filterAll', 'All Locations')}</SelectItem>
                        <SelectItem value="on">{t('neoStatus.filterOn', 'NEO ON')}</SelectItem>
                        <SelectItem value="pending">{t('neoStatus.filterPending', 'Upgrade Pending')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Rows — responsive list (no horizontal scroll, card-style on mobile) */}
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-500" /></div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-10 text-sm text-muted-foreground border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">
                    {t('neoStatus.noResults', 'No locations match your filters.')}
                </div>
            ) : (
                <div className="space-y-2" data-testid="neo-status-list">
                    {filtered.map((r, idx) => (
                        <div
                            key={r.location_id}
                            className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                            data-testid={`neo-row-${r.location_id}`}
                            data-insight-id={idx === 0 ? "neo.workflow.location-row" : undefined}
                            data-insight-label={idx === 0 ? "NEO location row" : undefined}
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{r.location_name}</p>
                                <p className="text-xs text-muted-foreground truncate">{r.organization_name || '—'}</p>
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    {t('neoStatus.lastUpdated', 'Last updated')}: {formatDate(r.last_toggled_at)}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 justify-between sm:justify-end">
                                {r.neo_enabled ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-xs font-semibold text-emerald-700 dark:text-emerald-300" data-testid={`neo-pill-on-${r.location_id}`}>
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                        {t('neoStatus.on', 'NEO ON')}
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/10 border border-slate-500/30 text-xs font-semibold text-slate-600 dark:text-slate-400" data-testid={`neo-pill-pending-${r.location_id}`}>
                                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                        {t('neoStatus.pending', 'Upgrade Pending')}
                                    </span>
                                )}
                                <Button
                                    size="sm"
                                    onClick={() => setPendingRow(r)}
                                    className={r.neo_enabled
                                        ? 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-white min-h-[36px]'
                                        : 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white min-h-[36px]'}
                                    data-testid={`neo-toggle-btn-${r.location_id}`}
                                    data-insight-id={idx === 0 ? "neo.workflow.toggle" : undefined}
                                    data-insight-label={idx === 0 ? "Enable / Disable NEO" : undefined}
                                >
                                    <Power className="w-3.5 h-3.5 mr-1" />
                                    {r.neo_enabled
                                        ? t('neoStatus.disableBtn', 'Disable NEO')
                                        : t('neoStatus.enableBtn', 'Enable NEO')}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Confirmation dialog */}
            <Dialog open={!!pendingRow} onOpenChange={(open) => !open && !submitting && setPendingRow(null)}>
                <DialogContent className="max-w-sm" data-testid="neo-confirm-dialog" data-insight-id="neo.workflow.confirm-toggle" data-insight-label="Confirm NEO change">
                    <DialogHeader>
                        <DialogTitle>
                            {pendingRow?.neo_enabled
                                ? t('neoStatus.confirmDisableTitle', 'Disable NEO for:')
                                : t('neoStatus.confirmEnableTitle', 'Enable NEO for:')}
                        </DialogTitle>
                        <DialogDescription className="pt-1">
                            <span className="block font-semibold text-base text-foreground">{pendingRow?.location_name}</span>
                            <span className="block text-xs text-muted-foreground mt-0.5">{pendingRow?.organization_name}</span>
                            <span className="block mt-3 text-sm">
                                {pendingRow?.neo_enabled
                                    ? t('neoStatus.confirmDisableBody', 'Managers will lose access to NEO controls for this location.')
                                    : t('neoStatus.confirmEnableBody', 'This will activate manager control features for this location.')}
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setPendingRow(null)}
                            disabled={submitting}
                            className="w-full sm:w-auto"
                            data-testid="neo-confirm-cancel"
                        >
                            <X className="w-4 h-4 mr-1" /> {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={confirmToggle}
                            disabled={submitting}
                            className={(pendingRow?.neo_enabled
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-fuchsia-600 hover:bg-fuchsia-700') + ' text-white w-full sm:w-auto'}
                            data-testid="neo-confirm-apply"
                        >
                            {submitting
                                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                : <Check className="w-4 h-4 mr-1" />}
                            {t('common.confirm', 'Confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
